import { ANNOUNCEMENT_ICON_XML as ICON } from '../../../shared/announcementIcons.js'
import { HERO_MOMENT_XML } from '../../../shared/heroMomentArt.js'
import Seo from '../components/Seo'
import MarketingNav from '../components/MarketingNav'
import MarketingFooter from '../components/MarketingFooter'
import PageHero from '../components/ds/PageHero'
import Section from '../components/ds/Section'
import SectionHead, { H2 } from '../components/ds/SectionHead'
import CtaBand from '../components/ds/CtaBand'
import Steps from '../components/ds/Steps'
import Button from '../components/ds/Button'
import Prose from '../components/ds/Prose'
import FaqAccordion from '../components/ds/FaqAccordion'
import { getCoverJsonLd } from '../lib/landingJsonLd'
import { dashboardUrl } from '../lib/dashboardUrl'

// /business/how-it-works — the workplace explainer, in the same shape as the
// worker one, because the two sides of a marketplace should be explained the
// same way.

const STEPS = [
  { art: HERO_MOMENT_XML.shiftPosted, title: 'Post the shift',
    body: 'The role, the day, the hours and what it pays. You see the cost before you commit.' },
  { art: HERO_MOMENT_XML.offerSent, title: 'It goes to the right people',
    body: 'Professionals who do that role, can get to you, and are free that day.' },
  { art: HERO_MOMENT_XML.ratesMatched, title: 'You see who is coming',
    body: "A name, how far they’re travelling and whether they have worked with you before." },
  { art: HERO_MOMENT_XML.toppedUp, title: 'Settle it afterwards',
    body: 'Hours come from their clock in and clock out. One bill, nothing to reconcile.' },
]

const FAQS = [
  { q: 'What if we need someone tomorrow morning?',
    a: "Post it. Short notice is the thing the old way is worst at, and it’s the reason most workplaces try us." },
  { q: "Can we set who we’ll and won’t have back?",
    a: 'Yes. Keep a list of the people who work well with you, and offer them shifts directly.' },
  { q: "Do we have to take whoever accepts first?",
    a: 'You decide how you want it to work. Some workplaces let a trusted person take it straight away, others want to look first.' },
  { q: 'How do we know the hours are right?',
    a: "They clock in and out on the day, on site. You’ve a day afterwards to raise anything that doesn’t look right." },
]

export default function CoverHowItWorks() {
  return (
    <div className="bg-canvas">
      <Seo
        title="How TempDash works for workplaces"
        description="Post a shift in a minute, see who is coming and where they are, then settle on one bill. Hours come from their own clock in and clock out."
        path="/business/how-it-works"
        jsonLd={getCoverJsonLd(FAQS, [['Get cover', '/business'], ['How it works', '/business/how-it-works']])}
      />
      <MarketingNav />

      {/* ⚠️ The ONE main landmark. Without it every <section> on the page
          sits outside any landmark and a screen reader user navigating by
          landmark cannot reach the content at all. The skip link in
          MarketingNav targets this id. */}
      <main id="main" tabIndex={-1} className="outline-none">

      <PageHero
        art={HERO_MOMENT_XML.overtimeApproved}
        eyebrow="Get cover"
        title="Post it, fill it, settle it."
        lead="No calls, no waiting for someone to ring back, and no finding out on the morning that no one is coming."
      >
        <Button href={dashboardUrl('/register')}>Post a shift</Button>
      </PageHero>

      <Section ground="canvas">
        <SectionHead eyebrow="Start to finish" title={<>Four steps, and three of them happen without you.</>} />
        <div className="mt-12"><Steps steps={STEPS} /></div>
      </Section>

      <Section ground="surface" width="text">
        <div className="space-y-12">
          <Prose id="rates" art={ICON.rate} title="Setting the rate">
            <p>
              You choose what the shift pays. We show you what similar shifts in your area are
              going for, so you are not guessing, and you can raise it if nothing is moving.
            </p>
            <p>
              Every pound you add goes to the person doing the work.
            </p>
          </Prose>

          <Prose id="who" art={ICON.compliance} title="Who can accept">
            <p>
              Only somebody qualified for that role, cleared to work, with their documents in date,
              and able to reach you. The checks happen before they can accept, not after they
              arrive.
            </p>
          </Prose>

          <Prose id="the-day" art={ICON.location} title="On the day">
            <p>
              They clock in on site when they arrive and clock out when they leave, and that is
              where their hours come from. You are not writing anything down or signing a sheet.
            </p>
            <p>
              If they stay later than the shift you agreed, you see it and you approve it.
            </p>
          </Prose>

          <Prose id="after" art={ICON.payday} title="After the shift">
            <p>
              You have a day to raise anything that does not look right. Then it settles, on one
              bill, with a record of every shift you can export whenever you need it.
            </p>
          </Prose>
        </div>
      </Section>

      <Section ground="surface" width="text">
        <H2>Questions, answered.</H2>
        <FaqAccordion items={FAQS} className="mt-10" />
      </Section>

      <CtaBand title="Try it with one shift.">
        <Button href={dashboardUrl('/register')}>Post a shift</Button>
      </CtaBand>

      </main>

      <MarketingFooter />
    </div>
  )
}
